import Decimal from './break_infinity';
import {customElement} from 'lit/decorators.js';
import {Actor} from './actor';
import {Creature} from './creature';
import {p5, ri} from './runner-idle';
import {assignIfDefined} from './utils';
import {Vector} from './Vector';
import {CreatureTemplates} from './creature_templates';
import {compose, equals, pluck} from 'ramda';

export class Spawner extends Actor {
  // #region Properties (1)

  override draw(p: p5, zoomScale: Decimal): void {
    if (ri.settings.map.showSpawners) {
      super.draw(p, zoomScale);
    }
  }

  template!: Partial<Creature>;
  /** Spawns new creatures between 0 and spawnRange from the Spawner. */
  spawnRange: Decimal = new Decimal(1);
  spawnInterval: Decimal = new Decimal(1);
  maxChildren: Decimal = new Decimal(1);
  #timeLeft: Decimal = new Decimal(0);
  setTimeLeft(value: Decimal): void {
    this.#timeLeft = value;
  }

  // #endregion Properties (1)
  override toJSON() {
    return {
      ...super.toJSON(),
      spawnRange: this.spawnRange.toString(),
      spawnInterval: this.spawnInterval.toString(),
      maxChildren: this.maxChildren,
      template: this.template.name,
      spawnTimeLeft: this.#timeLeft.toString(),
    };
  }

  // #region Public Static Methods (1)
  static override fromJSON(jsonObject): Spawner {
    const instance = Spawner.create({
      ...jsonObject,
      template: CreatureTemplates.get(jsonObject.template),
      position: new Vector(...jsonObject.position),
      interactionRange: new Decimal(jsonObject.interactionRange),
      bodySize: new Decimal(jsonObject.bodySize),
      maxChildren: new Decimal(jsonObject.maxChildren),
      spawnRange: new Decimal(jsonObject.spawnRange),
      spawnInterval: new Decimal(jsonObject.spawnInterval),
      resources: Object.fromEntries(
        Object.entries(jsonObject.resources).map(([key, value]) => [
          key,
          {
            name: key,
            value: new Decimal(value.value),
            capacity: new Decimal(value.capacity),
          },
        ])
      ),
      stats: Object.fromEntries(
        Object.entries(jsonObject.stats).map(([key, value]) => [
          key,
          {
            name: key,
            value: new Decimal(value.value),
          },
        ])
      ),
    });
    instance.setTimeLeft(new Decimal(jsonObject.spawnTimeLeft));
    return instance;
  }

  public static override create(
    overrides: Partial<Spawner> & {template: Partial<Creature>}
  ): Spawner {
    if (!overrides.template) {
      throw new Error('Spawner must have a template');
    }
    const instance = new Spawner();
    assignIfDefined(instance, {
      ...overrides,
      interactionRange: overrides.spawnRange,
    });
    instance.position$.next(overrides.position ?? instance.position);
    return instance;
  }

  // #endregion Public Static Methods (1)

  // #region Public Methods (1)

  public override act(): void {
    super.act();
    this.#timeLeft = this.#timeLeft.minus(1);
    if (this.#timeLeft.lte(0)) {
      if (
        this.maxChildren.gt(
          ri.actors.filter((actor) => actor.spawnedBy === this.name).length
        )
      ) {
        this.#timeLeft = this.spawnInterval;
        // console.log('spawning', this.template);
        const actor = Creature.create({
          ...this.template,
          spawnedBy: this.name,
          position: this.position$.value.plus(
            Vector.random(this.spawnRange.times(Math.random()))
          ),
        });
        ri.addActor(actor);
      }
    }
  }

  // #endregion Public Methods (1)
}
